import {
    bakeTransform,
    createChunkDataPool,
    dataTableToChunkSource,
    logger as splatTransformLogger,
    materializeToDataTable,
    MemoryFileSystem,
    processDataTable,
    stackLods,
    Transform,
    writeLodSource,
    writeSource,
    ZipFileSystem,
    type ChunkData,
    type ChunkDataPool,
    type ChunkLayer,
    type ChunkSource,
    type ChunkSourceMetadata,
    type DataTable,
    type ExtraColumn,
    type FileSystem,
    type LayerLayout,
    type LogEvent,
    type Options,
    type OutputFormat,
    type ReadRequest,
    type Renderer,
    type SHBands,
    type Writer
} from '@playcanvas/splat-transform';
import {
    defaultPostEffectSettings,
    type AnimTrack,
    type Annotation,
    type Camera,
    type CameraPose,
    type ExperienceSettings as BaseExperienceSettings,
    type PostEffectSettings
} from '@playcanvas/splat-transform/viewer-settings';
import {
    Mat3,
    Mat4,
    PIXELFORMAT_BGRA8,
    Quat,
    Texture,
    Vec3,
    WebgpuGraphicsDevice
} from 'playcanvas';

import { version } from '../package.json';
import type { AttributionPayload } from './attribution-data';
import { ColorGrade, createGradeTerms, dcDecode, dcEncode, sigmoid } from './color-grade';
import type { EditorSplatResource } from './editor-splat-resource';
import { Events } from './events';
import { groupInstancesByChunk } from './gaussian-instances';
import { PermutedChunkSource } from './io';
import { identitySceneTransform } from './jsi-scene-data';
import { layersFromSettings, sceneDataHash, writeScenePackage } from './jsi-scene-package';
import { canonicalSceneSettings } from './scene-data-camera';
import { SHRotation } from './sh-utils';
import { attributionFileSystem, getSogAttribution } from './sog-attribution';
import type { SpatialGuides } from './spatial-guides-data';
import { Splat } from './splat';
import { State } from './splat-state';

type SerializeSettings = {
    maxSHBands?: number;            // specifies the maximum number of bands to be exported
    selected?: boolean;             // only export selected gaussians. used for copy/paste
    minOpacity?: number;            // filter out gaussians with alpha less than or equal to minAlpha
    removeInvalid?: boolean;        // filter out gaussians with invalid data (NaN/Infinity)

    // the following options are used when serializing for document save.
    // keepWorldTransform flows through the streaming source; keepStateData
    // is accepted for compatibility but the streaming writers never export state.
    keepStateData?: boolean;        // keep the state data array
    keepWorldTransform?: boolean;   // don't apply the world transform when resolving splat transforms
    keepColorTint?: boolean;        // refrain from applying color tints
    forceSHBands?: number;          // internal: force exported SH bands for multi-source LOD stacking
};

type CameraPoseOverlayTransform = {
    position?: [number, number, number],
    rotation?: [number, number, number],
    scale?: number
};

type CameraPoseOverlay = {
    id: string,
    position: [number, number, number],
    target: [number, number, number],
    fov: number,
    thumbnailUrl?: string,
    imageUrl?: string,
    label?: string,
    group?: string
};
type RevealEffectSettings = {
    type: 'none' | 'radial',
    duration: number,
    delay?: number
};
type ExperienceSettings = BaseExperienceSettings & {
    spatialGuides?: SpatialGuides,
    cameraPoses: CameraPoseOverlay[],
    cameraPosesSpace?: 'viewer' | 'colmap',
    cameraPosesInvertDirection?: boolean,
    cameraPosesTransform?: CameraPoseOverlayTransform,
    cameraPosesScale?: number,
    cameraPosesLineWidth?: number,
    cameraPosesOpacity?: number,
    cameraPosesInactiveOpacity?: number,
    cameraOverlayIconUrl?: string,
    revealEffect?: RevealEffectSettings,
};
type ViewerExportSettings = {
    type: 'html' | 'zip';
    content: 'standalone' | 'streamed-lod';
    lodSource: 'generate' | 'visible-splats';
    lodLevels: number;
    lodDecimationPercent: number;
    lodSplatIndices?: number[];
    experienceSettings: ExperienceSettings;
    events?: Events;
};

type ProgressFunc = (loaded: number, total: number) => void;

const shBandCoeffs = [0, 3, 8, 15];

// calculate splat transforms on demand and cache the result for next time
class SplatTransformCache {
    getMat: (index: number) => Mat4;
    getRot: (index: number) => Quat;
    getScale: (index: number) => Vec3;
    getSHRot: (index: number) => SHRotation;

    constructor(splat: Splat, keepWorldTransform = false) {
        const transforms = new Map<number, { transformIndex: number, mat: Mat4, rot: Quat, scale: Vec3, shRot: SHRotation }>();
        const { instances } = splat;
        const tmpMat = new Mat4();
        const tmpMat3 = new Mat3();
        const tmpQuat = new Quat();

        // `index` is the instance being exported; the cache itself is keyed by
        // palette entry, which is what makes it a cache
        const getTransform = (index: number) => {
            const transformIndex = instances.transformIndex(index);
            let result = transforms.get(transformIndex);
            if (!result) {
                result = { transformIndex, mat: null, rot: null, scale: null, shRot: null };
                transforms.set(transformIndex, result);
            }
            return result;
        };

        this.getMat = (index: number) => {
            const transform = getTransform(index);

            if (!transform.mat) {
                const mat = new Mat4();

                // we must undo the transform we apply at load time to output data
                if (!keepWorldTransform) {
                    mat.setFromEulerAngles(0, 0, -180);
                    mat.mul2(mat, splat.entity.getWorldTransform());
                }

                // combine with transform palette matrix
                if (transform.transformIndex > 0) {
                    splat.transformPalette.getTransform(transform.transformIndex, tmpMat);
                    mat.mul2(mat, tmpMat);
                }

                transform.mat = mat;
            }

            return transform.mat;
        };

        this.getRot = (index: number) => {
            const transform = getTransform(index);

            if (!transform.rot) {
                transform.rot = new Quat().setFromMat4(this.getMat(index));
            }

            return transform.rot;
        };

        this.getScale = (index: number) => {
            const transform = getTransform(index);

            if (!transform.scale) {
                const scale = new Vec3();
                this.getMat(index).getScale(scale);
                transform.scale = scale;
            }

            return transform.scale;
        };

        this.getSHRot = (index: number) => {
            const transform = getTransform(index);

            if (!transform.shRot) {
                tmpQuat.setFromMat4(this.getMat(index));
                tmpMat3.setFromQuat(tmpQuat);
                transform.shRot = new SHRotation(tmpMat3);
            }

            return transform.shRot;
        };
    }
}

// Resolve an instance's baked colour grade on demand and cache it, the colour
// analogue of SplatTransformCache. Keyed by colour palette entry, so a scene
// where every gaussian shares one grade costs one ColorGrade.
class ColorGradeCache {
    get: (index: number) => ColorGrade;

    constructor(splat: Splat) {
        const grades = new Map<number, ColorGrade>();
        const { instances } = splat;
        const entry = createGradeTerms();

        this.get = (index: number) => {
            const colorIndex = instances.colorIndex(index);
            let result = grades.get(colorIndex);
            if (!result) {
                splat.colorPalette.getEntry(colorIndex, entry);
                result = new ColorGrade(entry);
                grades.set(colorIndex, result);
            }
            return result;
        };
    }
}

// Number of f_rest_* SH coefficients per band level (mirrors splat-transform's
// SH_REST_COUNTS; that constant isn't exported from the package root).
const SH_REST_COUNTS: Record<number, number> = { 0: 0, 1: 9, 2: 24, 3: 45 };

// Gaussians per chunk when streaming a scene to splat-transform. Chosen to
// bound the transient working set (input layer buffers + writer output buffer)
// rather than scale with the whole scene.
const EXPORT_CHUNK_SIZE = 256 * 1024;

// Build the canonical per-layer byte layout splat-transform expects. The
// interleaved packing here must match splat-transform's readers/materialize:
// position = xyz (stride 12); geometric = rot0-3, scale0-2, opacity (stride 32);
// color = dc0-2 then f_rest_* (stride (3 + numRest) * 4).
const buildLayouts = (numRest: number): Partial<Record<ChunkLayer, LayerLayout>> => ({
    position: {
        stride: 12,
        fields: { position: { byteOffset: 0, components: 3, type: 'float32' } }
    },
    geometric: {
        stride: 32,
        fields: {
            rotation: { byteOffset: 0, components: 4, type: 'float32' },
            scale: { byteOffset: 16, components: 3, type: 'float32' },
            opacity: { byteOffset: 28, components: 1, type: 'float32' }
        }
    },
    color: {
        stride: (3 + numRest) * 4,
        fields: numRest > 0 ? {
            dc: { byteOffset: 0, components: 3, type: 'float32' },
            shRest: { byteOffset: 12, components: numRest, type: 'float32' }
        } : {
            dc: { byteOffset: 0, components: 3, type: 'float32' }
        }
    }
});

const buildOtherLayout = (extraColumns: ExtraColumn[]): LayerLayout => ({
    stride: extraColumns.length * 4,
    fields: Object.fromEntries(extraColumns.map((column, index) => [column.name, {
        byteOffset: index * 4,
        components: 1,
        type: column.type
    }]))
});
const sourceChunkCount = (source: ChunkSource, chunkIndex: number) => {
    return Math.min(source.meta.chunkSize, source.meta.numGaussians - chunkIndex * source.meta.chunkSize);
};

const validFloatLayer = (chunk: ChunkData, row: number) => {
    const values = new Float32Array(chunk.data, row * chunk.stride, chunk.stride / 4);
    for (let i = 0; i < values.length; ++i) {
        if (!Number.isFinite(values[i])) return false;
    }
    return true;
};

const validGeometric = (chunk: ChunkData, row: number) => {
    const values = new Float32Array(chunk.data, row * chunk.stride, chunk.stride / 4);
    for (let i = 0; i < 4; ++i) if (!Number.isFinite(values[i])) return false;
    for (let i = 4; i < 7; ++i) {
        if (!Number.isFinite(values[i]) && values[i] !== -Infinity) return false;
    }
    return !Number.isNaN(values[7]);
};

const validOther = (chunk: ChunkData, row: number) => {
    const view = new DataView(chunk.data);
    for (const field of Object.values(chunk.fields)) {
        if (field.type !== 'float32') continue;
        for (let i = 0; i < field.components; ++i) {
            const value = view.getFloat32(row * chunk.stride + field.byteOffset + i * 4, true);
            if (!Number.isFinite(value)) return false;
        }
    }
    return true;
};

const filteredIndices = async (splat: Splat, settings: SerializeSettings) => {
    const { source } = splat.resource;
    const state = splat.instances.flags;
    const numInstances = splat.instances.count;
    const onlySelected = settings.selected ?? false;
    const minOpacity = settings.minOpacity ?? 0;
    const removeInvalid = settings.removeInvalid ?? false;
    const needsSource = minOpacity > 0 || removeInvalid;

    if (!needsSource) {
        let count = 0;
        for (let i = 0; i < numInstances; ++i) {
            if (!onlySelected || state[i] === State.selected) count++;
        }
        const result = new Uint32Array(count);
        for (let i = 0, dst = 0; i < numInstances; ++i) {
            if (!onlySelected || state[i] === State.selected) result[dst++] = i;
        }
        return result;
    }

    const pool = createChunkDataPool({ chunkSize: source.meta.chunkSize });
    // the source has to be read sequentially, which visits instances in source
    // order, so acceptance is recorded in a mask and emitted in instance order
    const { starts, ordered } = groupInstancesByChunk(splat.instances, source.meta.chunkSize, source.meta.numChunks[0]);
    const accepted = new Uint8Array(numInstances);
    let total = 0;
    try {
        for (let chunkIndex = 0; chunkIndex < source.meta.numChunks[0]; ++chunkIndex) {
            const count = sourceChunkCount(source, chunkIndex);
            const position = removeInvalid ? pool.acquire('position', source.meta.layouts.position, count) : undefined;
            const geometric = pool.acquire('geometric', source.meta.layouts.geometric, count);
            const color = removeInvalid ? pool.acquire('color', source.meta.layouts.color, count) : undefined;
            const other = removeInvalid && source.meta.availableLayers.has('other') ? pool.acquire('other', source.meta.layouts.other, count) : undefined;
            try {
                await source.read({ chunkIndex, position, geometric, color, other });
                const opacity = new Float32Array(geometric.data);
                const base = chunkIndex * source.meta.chunkSize;
                for (let slot = starts[chunkIndex]; slot < starts[chunkIndex + 1]; ++slot) {
                    const instance = ordered[slot];
                    if (onlySelected && state[instance] !== State.selected) continue;
                    const i = splat.instances.sourceRow[instance] - base;
                    if (minOpacity > 0 && sigmoid(opacity[i * 8 + 7]) < minOpacity) continue;
                    if (removeInvalid && (!validFloatLayer(position, i) || !validGeometric(geometric, i) ||
                        !validFloatLayer(color, i) || (other && !validOther(other, i)))) continue;
                    accepted[instance] = 1;
                    total++;
                }
            } finally {
                position?.release();
                geometric.release();
                color?.release();
                other?.release();
            }
        }
    } finally {
        pool.destroy();
    }

    const result = new Uint32Array(total);
    for (let i = 0, dst = 0; i < numInstances; ++i) {
        if (accepted[i]) result[dst++] = i;
    }
    return result;
};

type ExportEntry = {
    splat: Splat;
    indices: Uint32Array;
    start: number;
    end: number;
    transform: SplatTransformCache;
    grade: ColorGradeCache;
};

/**
 * A lazy, chunked ChunkSource over a set of Splats, for feeding splat-transform's
 * streaming writers (writeSource) without materializing a whole-scene copy.
 *
 * It is the streaming analog of the old extractDataTable/DataTable path:
 * gaussians are filtered
 * (selection/opacity/invalid) and transformed (world + palette + SH
 * rotation + colour tint + PLY-space flip) on demand via SingleSplat, one chunk
 * at a time. The output is in PLY space, so the source is tagged Transform.PLY
 * (identity) and the writers' bakeTransform is a no-op.
 */
class SuperSplatChunkSource implements ChunkSource {
    meta: ChunkSourceMetadata;

    private entries: ExportEntry[];
    private settings: SerializeSettings;
    private numRest: number;
    private pools = new Map<Splat, ChunkDataPool>();

    private constructor(entries: ExportEntry[], settings: SerializeSettings, outputBands: number) {
        this.entries = entries;
        this.settings = settings;
        this.numRest = SH_REST_COUNTS[outputBands];
        const total = entries.length ? entries[entries.length - 1].end : 0;

        const extraColumns: ExtraColumn[] = [...new Set(entries.map(entry => entry.splat.resource.customChannelName).filter(Boolean))]
        .map(name => ({ name, type: 'float32' }));

        const numChunks = Math.ceil(total / EXPORT_CHUNK_SIZE);
        this.meta = {
            numGaussians: total,
            numLods: 1,
            lodCounts: [total],
            chunkSize: EXPORT_CHUNK_SIZE,
            numChunks: [numChunks],
            shBands: outputBands as SHBands,
            // supersplat's edit pipeline doesn't carry the trained-model tag
            // (antialiased / 2dgs) through load, so exports are untagged
            model: 'default',
            extraColumns,
            transform: Transform.PLY,
            availableLayers: new Set<ChunkLayer>([
                'position', 'geometric', 'color',
                ...(extraColumns.length > 0 ? ['other' as ChunkLayer] : [])
            ]),
            layouts: {
                ...buildLayouts(this.numRest),
                ...(extraColumns.length > 0 ? { other: buildOtherLayout(extraColumns) } : {})
            }
        };
    }

    static async create(splats: Splat[], settings: SerializeSettings) {
        const outputBands = Math.min(settings.maxSHBands ?? 3, settings.forceSHBands ?? (splats.length ? Math.max(...splats.map(s => s.resource.shBands)) : 0));
        const entries: ExportEntry[] = [];
        let start = 0;
        for (const splat of splats) {
            const indices = await filteredIndices(splat, settings);
            const end = start + indices.length;
            entries.push({
                splat,
                indices,
                start,
                end,
                transform: new SplatTransformCache(splat, settings.keepWorldTransform),
                grade: new ColorGradeCache(splat)
            });
            start = end;
        }
        return new SuperSplatChunkSource(entries, settings, outputBands);
    }

    private findEntry(row: number) {
        let lo = 0;
        let hi = this.entries.length - 1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            const entry = this.entries[mid];
            if (row < entry.start) hi = mid - 1;
            else if (row >= entry.end) lo = mid + 1;
            else return entry;
        }
        return null;
    }

    // `instanceIndices` selects instances of `entry.splat`; the static data is
    // gathered by their source rows, while the palette and grade lookups stay in
    // instance space (two instances of one row can carry different transforms)
    private async readGroup(entry: ExportEntry, instanceIndices: Uint32Array, outputOffset: number, request: ReadRequest) {
        const { splat, transform, grade: gradeCache } = entry;
        const source = splat.resource.source;
        const { sourceRow } = splat.instances;
        const indices = new Uint32Array(instanceIndices.length);
        for (let i = 0; i < instanceIndices.length; ++i) {
            indices[i] = sourceRow[instanceIndices[i]];
        }
        let pool = this.pools.get(splat);
        if (!pool) {
            pool = createChunkDataPool({ chunkSize: source.meta.chunkSize });
            this.pools.set(splat, pool);
        }

        const count = indices.length;
        const position = request.position ? pool.acquire('position', source.meta.layouts.position, count) : undefined;
        const geometric = request.geometric ? pool.acquire('geometric', source.meta.layouts.geometric, count) : undefined;
        const color = request.color ? pool.acquire('color', source.meta.layouts.color, count) : undefined;
        const other = request.other && source.meta.availableLayers.has('other') ? pool.acquire('other', source.meta.layouts.other, count) : undefined;
        try {
            await source.read({ indices, indexOffset: 0, count, position, geometric, color, other });
            const srcPosition = position ? new Float32Array(position.data) : null;
            const srcGeometric = geometric ? new Float32Array(geometric.data) : null;
            const srcColor = color ? new Float32Array(color.data) : null;
            const dstPosition = request.position ? new Float32Array(request.position.data) : null;
            const dstGeometric = request.geometric ? new Float32Array(request.geometric.data) : null;
            const dstColor = request.color ? new Float32Array(request.color.data) : null;
            const srcRest = SH_REST_COUNTS[source.meta.shBands];
            const srcCoeffs = shBandCoeffs[source.meta.shBands];
            const dstCoeffs = this.numRest / 3;
            const rest = new Float32Array(this.numRest);
            const tmpSH = new Float32Array(dstCoeffs);
            const positionValue = new Vec3();
            const rotationValue = new Quat();
            const c = { r: 0, g: 0, b: 0 };

            for (let i = 0; i < count; ++i) {
                const instance = instanceIndices[i];
                if (request.other) {
                    const output = new DataView(request.other.data);
                    const input = other ? new DataView(other.data) : null;
                    for (const [name, field] of Object.entries(request.other.fields)) {
                        const sourceField = other?.fields[name];
                        const src = i * (other?.stride ?? 0) + (sourceField?.byteOffset ?? 0);
                        const value = sourceField ? (sourceField.type === 'uint32' ? input.getUint32(src, true) : input.getFloat32(src, true)) : 0;
                        output.setFloat32((outputOffset + i) * request.other.stride + field.byteOffset, value, true);
                    }
                }
                if (dstPosition) {
                    const src = i * 3;
                    const dst = (outputOffset + i) * 3;
                    positionValue.set(srcPosition[src], srcPosition[src + 1], srcPosition[src + 2]);
                    transform.getMat(instance).transformPoint(positionValue, positionValue);
                    dstPosition[dst] = positionValue.x;
                    dstPosition[dst + 1] = positionValue.y;
                    dstPosition[dst + 2] = positionValue.z;
                }
                if (dstGeometric) {
                    const src = i * 8;
                    const dst = (outputOffset + i) * 8;
                    rotationValue.set(srcGeometric[src + 1], srcGeometric[src + 2], srcGeometric[src + 3], srcGeometric[src]);
                    rotationValue.mul2(transform.getRot(instance), rotationValue);
                    dstGeometric[dst] = rotationValue.w;
                    dstGeometric[dst + 1] = rotationValue.x;
                    dstGeometric[dst + 2] = rotationValue.y;
                    dstGeometric[dst + 3] = rotationValue.z;
                    const scale = transform.getScale(instance);
                    dstGeometric[dst + 4] = Math.log(Math.exp(srcGeometric[src + 4]) * scale.x);
                    dstGeometric[dst + 5] = Math.log(Math.exp(srcGeometric[src + 5]) * scale.y);
                    dstGeometric[dst + 6] = Math.log(Math.exp(srcGeometric[src + 6]) * scale.z);
                    const grade = gradeCache.get(instance);
                    dstGeometric[dst + 7] = grade.hasTransparency ?
                        grade.applyOpacity(srcGeometric[src + 7]) : srcGeometric[src + 7];
                }
                if (dstColor) {
                    const grade = gradeCache.get(instance);
                    const src = i * (3 + srcRest);
                    const dst = (outputOffset + i) * (3 + this.numRest);
                    c.r = dcDecode(srcColor[src]);
                    c.g = dcDecode(srcColor[src + 1]);
                    c.b = dcDecode(srcColor[src + 2]);
                    if (grade.hasTint) grade.applyDC(c);
                    dstColor[dst] = dcEncode(c.r);
                    dstColor[dst + 1] = dcEncode(c.g);
                    dstColor[dst + 2] = dcEncode(c.b);

                    rest.fill(0);
                    if (dstCoeffs > 0) {
                        for (let channel = 0; channel < 3; ++channel) {
                            tmpSH.fill(0);
                            const copy = Math.min(srcCoeffs, dstCoeffs);
                            for (let coeff = 0; coeff < copy; ++coeff) {
                                tmpSH[coeff] = srcColor[src + 3 + channel * srcCoeffs + coeff];
                            }
                            transform.getSHRot(instance).apply(tmpSH);
                            rest.set(tmpSH, channel * dstCoeffs);
                        }
                        if (grade.hasTint) {
                            for (let coeff = 0; coeff < dstCoeffs; ++coeff) {
                                c.r = rest[coeff];
                                c.g = rest[dstCoeffs + coeff];
                                c.b = rest[dstCoeffs * 2 + coeff];
                                grade.applySH(c);
                                rest[coeff] = c.r;
                                rest[dstCoeffs + coeff] = c.g;
                                rest[dstCoeffs * 2 + coeff] = c.b;
                            }
                        }
                        dstColor.set(rest, dst + 3);
                    }
                }
            }
        } finally {
            position?.release();
            geometric?.release();
            color?.release();
            other?.release();
        }
    }

    async read(request: ReadRequest): Promise<void> {
        const isGather = 'indices' in request;
        const anyBuf = (request.position ?? request.geometric ?? request.color ?? request.other) as ChunkData;
        const count = isGather ? request.count : anyBuf.count;
        const chunkBase = isGather ? 0 : request.chunkIndex * EXPORT_CHUNK_SIZE;

        let offset = 0;
        while (offset < count) {
            const outputRow = isGather ? request.indices[request.indexOffset + offset] : chunkBase + offset;
            const entry = this.findEntry(outputRow);
            if (!entry) throw new Error(`Invalid export row ${outputRow}`);
            const maxCount = entry.splat.resource.source.meta.chunkSize;
            let groupCount = 1;
            while (offset + groupCount < count && groupCount < maxCount) {
                const nextRow = isGather ? request.indices[request.indexOffset + offset + groupCount] : chunkBase + offset + groupCount;
                if (this.findEntry(nextRow) !== entry) break;
                groupCount++;
            }
            const instanceIndices = new Uint32Array(groupCount);
            for (let i = 0; i < groupCount; ++i) {
                const row = isGather ? request.indices[request.indexOffset + offset + i] : chunkBase + offset + i;
                instanceIndices[i] = entry.indices[row - entry.start];
            }
            await this.readGroup(entry, instanceIndices, offset, request);
            offset += groupCount;
        }
    }

    close(): Promise<void> {
        this.pools.forEach(pool => pool.destroy());
        this.pools.clear();
        return Promise.resolve();
    }
}

/**
 * Build a ChunkSource + matching pool over the given splats, or null if nothing
 * passes the export filter.
 */
const createExportSource = async (splats: Splat[], settings: SerializeSettings): Promise<{ source: ChunkSource, pool: ChunkDataPool } | null> => {
    const source = await SuperSplatChunkSource.create(splats, settings);
    if (source.meta.numGaussians === 0) {
        return null;
    }
    const pool = createChunkDataPool({ chunkSize: source.meta.chunkSize });
    return { source, pool };
};

// Thrown when the WebGPU device needed for SOG compression can't be created.
// Callers show a friendly message for this instead of the raw error text.
class WebGPUUnavailableError extends Error {
    constructor() {
        super('WebGPU is not available');
        this.name = 'WebGPUUnavailableError';
    }
}

// Cached WebGPU device for SOG compression
let cachedGpuDevice: WebgpuGraphicsDevice | null = null;
let cachedBackbuffer: Texture | null = null;

const createGpuDevice = async (): Promise<WebgpuGraphicsDevice> => {
    if (cachedGpuDevice) {
        return cachedGpuDevice;
    }

    if (!navigator.gpu) {
        throw new WebGPUUnavailableError();
    }

    // Create a minimal canvas for the graphics device
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;

    const graphicsDevice = new WebgpuGraphicsDevice(canvas, {
        antialias: false,
        depth: false,
        stencil: false,
        powerPreference: 'high-performance'
    });

    try {
        await graphicsDevice.createDevice();
    } catch (err) {
        // createDevice fails with an obscure internal error when no adapter
        // is available (e.g. blocklisted GPU or missing drivers)
        console.error(err);
        throw new WebGPUUnavailableError();
    }

    // createDevice can also resolve without creating a device (e.g.
    // blocklisted adapters)
    // @ts-ignore - wgpu is an internal property
    if (!graphicsDevice.wgpu) {
        throw new WebGPUUnavailableError();
    }

    // Create external backbuffer (required by PlayCanvas)
    cachedBackbuffer = new Texture(graphicsDevice, {
        width: 1024,
        height: 512,
        name: 'SogComputeBackbuffer',
        mipmaps: false,
        format: PIXELFORMAT_BGRA8
    });

    // @ts-ignore - externalBackbuffer is an internal property
    graphicsDevice.externalBackbuffer = cachedBackbuffer;

    cachedGpuDevice = graphicsDevice;
    return graphicsDevice;
};

/**
 * Stream the given splats to a file via splat-transform's writeSource. Streaming
 * formats (ply/sog/splat) never build a whole-scene copy; the rest materialize a
 * single transient copy inside the library.
 */
const writeSplatFile = async (
    splats: Splat[],
    settings: SerializeSettings,
    outputFormat: OutputFormat,
    filename: string,
    options: Options,
    fs: FileSystem
): Promise<void> => {
    const built = await createExportSource(splats, settings);
    if (!built) {
        return;
    }
    const { source, pool } = built;
    try {
        await writeSource({ filename, outputFormat, source, pool, options, createDevice: createGpuDevice }, attributionFileSystem(fs));
    } finally {
        await source.close();
        pool.destroy();
    }
};

const writeZipFromMemory = async (memFs: MemoryFileSystem, fs: FileSystem): Promise<void> => {
    const zipWriter = await fs.createWriter('output.zip');
    const zipFs = new ZipFileSystem(zipWriter);
    try {
        for (const [filename, data] of memFs.results.entries()) {
            const writer = await zipFs.createWriter(filename);
            await writer.write(data);
            await writer.close();
        }
    } finally {
        await zipFs.close();
    }
};

const writeMemoryFile = async (fs: FileSystem, filename: string, data: Uint8Array): Promise<void> => {
    const writer = await fs.createWriter(filename);
    await writer.write(data);
    await writer.close();
};

const assertViewerCapabilities = (script: string) => {
    if (!script.includes('// JSI_VIEWER_SCENE_REFERENCES=2') || !script.includes('// JSI_VIEWER_SCENE_DATA=1') || !script.includes('// JSI_VIEWER_ATTRIBUTION=1')) {
        throw new Error('The bundled viewer is outdated and cannot load the current Scene References / JSI Scene Data format. Rebuild the JSI viewer, then rebuild/redeploy the editor and reload it before exporting.');
    }
};

const fetchStaticViewerAsset = async (filename: string): Promise<Uint8Array> => {
    // Export assets must not come from an older HTTP cache entry: a current
    // editor paired with the distance-only viewer produces an unreadable file.
    const response = await fetch(new URL(`static/jsi-viewer/${filename}`, document.baseURI), { cache: 'no-store' });
    if (!response.ok) {
        throw new Error(`Failed to load JSI viewer export asset '${filename}': ${response.status} ${response.statusText}`);
    }
    const bytes = new Uint8Array(await response.arrayBuffer());
    if (filename === 'index.js') assertViewerCapabilities(new TextDecoder().decode(bytes));
    return bytes;
};

// Use the viewer's bootstrap seam instead of rewriting its implementation.
// Escaping '<' also prevents annotation text from terminating the JSON script.
const setViewerBootstrap = (html: string, bootstrap: object): string => {
    const json = JSON.stringify(bootstrap).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
    const seam = /(<script type="application\/json" id="sse-bootstrap">)[\s\S]*?(<\/script>)/;
    if (!seam.test(html)) throw new Error('JSI viewer bootstrap seam is missing');
    return html.replace(seam, (_match, open, close) => `${open}${json}${close}`);
};

const patchJsiViewerHtml = (html: string, contentUrl: string): string => {
    return setViewerBootstrap(html, { contentUrl: `./${contentUrl}` });
};

const inlineJsiViewer = (html: string, css: string, js: string, bootstrap: object, logoUri: string, heatmapUri: string, cameraUri: string): string => {
    assertViewerCapabilities(js);
    const moduleSeam = /<script type="module">\s*import\s+\{\s*main\s*\}\s+from\s+['"]\.\/index\.js['"];?/;
    if (!moduleSeam.test(html)) throw new Error('JSI viewer module seam is missing');
    const viewerJs = js
    .replace(/\nexport \{ main \};[\s\S]*$/, '')
    .replace(/(['"])\.\/icons\/camera\.svg\1/g, (_match, quote) => `${quote}${cameraUri}${quote}`)
    .replace(/<\/script/gi, '<\\/script');
    return setViewerBootstrap(html, bootstrap)
    .replace('<link rel="stylesheet" href="./index.css" />', () => `<style>${css}</style>`)
    .replace('./icons/jsi-logo.png', () => logoUri)
    .replace('./icons/jsi-icon-heatmap.svg', () => heatmapUri)
    .replace(moduleSeam, () => `<script type="module">\n${viewerJs}`);
};

const packageSceneSettings = async (settings: ExperienceSettings, bakedAsset?: Uint8Array, attribution: AttributionPayload[] = []) => {
    const canonical = canonicalSceneSettings(settings as unknown as Record<string, unknown>);
    const hash = bakedAsset ? await sceneDataHash(bakedAsset) : undefined;
    const assets = hash ? [{
        id: hash, revision: hash, sha256: hash, representation: 'scene-baked' as const, sourceToScene: identitySceneTransform()
    }] : [];
    const layers = layersFromSettings(canonical);
    if (hash && attribution.length) layers.attribution = attribution.map(payload => ({ assetId: hash, payload }));
    const jsiSceneData = await writeScenePackage(layers, hash ? `scene-${hash}` : crypto.randomUUID(), assets);
    return { ...settings, jsiSceneData };
};

const writeJsiViewerPackage = async (
    fs: MemoryFileSystem,
    experienceSettings: ExperienceSettings,
    contentUrl: string
): Promise<void> => {
    const encoder = new TextEncoder();
    const html = new TextDecoder().decode(await fetchStaticViewerAsset('index.html'));
    await writeMemoryFile(fs, 'index.html', encoder.encode(patchJsiViewerHtml(html, contentUrl)));
    await writeMemoryFile(fs, 'index.css', await fetchStaticViewerAsset('index.css'));
    await writeMemoryFile(fs, 'index.js', await fetchStaticViewerAsset('index.js'));
    const packagedSettings = await packageSceneSettings(experienceSettings, contentUrl.endsWith('.json') ? undefined : fs.results.get(contentUrl), getSogAttribution(fs, contentUrl));
    await writeMemoryFile(fs, 'settings.json', encoder.encode(JSON.stringify(packagedSettings, null, 4)));

    const optionalAssets = [
        'index.js.map',
        'icons/jsi-logo.png',
        'icons/jsi-logo.svg',
        'icons/camera.svg',
        'icons/jsi-icon-heatmap.svg'
    ];

    for (const asset of optionalAssets) {
        try {
            await writeMemoryFile(fs, asset, await fetchStaticViewerAsset(asset));
        } catch (err) {
            console.warn(err);
        }
    }
};

const bytesToBase64 = (bytes: Uint8Array): string => {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunkSize, bytes.length)));
    }
    return btoa(binary);
};

// The splat-transform html-bundle writer embeds the stock SuperSplat viewer.
// Build the single-file variant from the already-built JSI viewer instead.
const writeJsiViewerHtml = async (
    splats: Splat[],
    settings: SerializeSettings,
    experienceSettings: ExperienceSettings,
    fs: FileSystem
): Promise<void> => {
    const viewerFs = new MemoryFileSystem();
    await writeSplatFile(splats, settings, 'sog-bundle', 'scene.sog', { iterations: 10 }, viewerFs);

    const [htmlBytes, cssBytes, jsBytes, cameraBytes, logoBytes, heatmapBytes] = await Promise.all([
        fetchStaticViewerAsset('index.html'),
        fetchStaticViewerAsset('index.css'),
        fetchStaticViewerAsset('index.js'),
        fetchStaticViewerAsset('icons/camera.svg'),
        fetchStaticViewerAsset('icons/jsi-logo.png'),
        fetchStaticViewerAsset('icons/jsi-icon-heatmap.svg')
    ]);

    const scene = viewerFs.results.get('scene.sog');
    if (!scene) throw new Error('Failed to create the SOG payload for the JSI HTML viewer.');

    const dataUri = `data:application/octet-stream;base64,${bytesToBase64(scene)}`;
    const cameraUri = `data:image/svg+xml;base64,${bytesToBase64(cameraBytes)}`;
    const logoUri = `data:image/png;base64,${bytesToBase64(logoBytes)}`;
    const heatmapUri = `data:image/svg+xml;base64,${bytesToBase64(heatmapBytes)}`;
    const bootstrap = {
        settings: await packageSceneSettings({ ...experienceSettings, cameraOverlayIconUrl: cameraUri }, scene, getSogAttribution(viewerFs, 'scene.sog')),
        contentUrl: dataUri,
        contentFilename: 'scene.sog'
    };

    const html = inlineJsiViewer(
        new TextDecoder().decode(htmlBytes), new TextDecoder().decode(cssBytes),
        new TextDecoder().decode(jsBytes), bootstrap, logoUri, heatmapUri, cameraUri
    );

    await writeMemoryFile(fs, 'output.html', new TextEncoder().encode(html));
};

const materializeExportSource = async (splats: Splat[], settings: SerializeSettings): Promise<{ dataTable: DataTable, chunkSize: number } | null> => {
    const built = await createExportSource(splats, settings);
    if (!built) {
        return null;
    }

    const { source, pool } = built;
    try {
        return {
            dataTable: await materializeToDataTable(source, pool),
            chunkSize: pool.chunkSize
        };
    } finally {
        await source.close();
        pool.destroy();
    }
};

const createGeneratedLodSource = async (
    splats: Splat[],
    settings: SerializeSettings,
    levels: number,
    decimationPercent: number
): Promise<ChunkSource | null> => {
    const sources: ChunkSource[] = [];
    const clampedLevels = Math.max(1, Math.min(4, Math.floor(levels)));
    const clampedPercent = Math.max(1, Math.min(99, decimationPercent));

    for (let level = 0; level < clampedLevels; ++level) {
        const materialized = await materializeExportSource(splats, settings);
        if (!materialized) {
            return null;
        }

        let { dataTable } = materialized;
        if (level > 0) {
            const keepPercent = Math.max(1, Math.min(100, 100 * Math.pow(clampedPercent / 100, level)));
            dataTable = await processDataTable(dataTable, [{
                kind: 'decimate',
                count: null,
                percent: keepPercent
            }], { createDevice: createGpuDevice });
        }

        sources.push(bakeTransform(dataTableToChunkSource(dataTable, materialized.chunkSize), Transform.PLY));
    }

    return sources.length > 0 ? stackLods(sources) : null;
};

const createVisibleSplatLodSource = async (splats: Splat[], settings: SerializeSettings, levels: number, indices?: number[]): Promise<ChunkSource | null> => {
    const sources: ChunkSource[] = [];
    const requestedCount = Math.max(1, Math.min(4, Math.floor(levels), splats.length));
    const selectedSplats = (indices?.length ? indices : splats.map((_, i) => i))
    .slice(0, requestedCount)
    .map(index => splats[index])
    .filter(Boolean);
    const count = selectedSplats.length;
    const forceSHBands = Math.min(settings.maxSHBands ?? 3, selectedSplats.length ? Math.max(...selectedSplats.map(splat => splat.resource.shBands)) : 0);
    const lodSettings = {
        ...settings,
        forceSHBands
    };

    for (let i = 0; i < count; ++i) {
        const built = await createExportSource([selectedSplats[i]], lodSettings);
        if (built) {
            sources.push(bakeTransform(built.source, Transform.PLY));
            built.pool.destroy();
        }
    }

    return sources.length > 0 ? stackLods(sources) : null;
};

/**
 * Write a resource's static gaussian data, restricted to `rows` (in-memory row
 * indices, ascending) and in that order. Nothing is baked - no entity transform,
 * no palette, no grade - because a .ssproj stores the static tier untouched and
 * keeps every per-layer edit in a side blob. The saved entity rotation already
 * carries the import transform the reader reported (LCC reports one; the other
 * formats report `Transform.PLY`), so the rows are written verbatim: the source
 * is relabelled as PLY space and the writer's bakeTransform becomes a no-op.
 *
 * The source is NOT closed here - it belongs to the resource and outlives the save.
 */
const writeResourceFile = async (
    resource: EditorSplatResource,
    rows: Uint32Array,
    filename: string,
    fs: FileSystem,
    settings?: { format?: 'ply' | 'sog', maxSHBands?: number, iterations?: number }
): Promise<Uint32Array> => {
    const retained = resource.source;

    // PermutedChunkSource gathers through an order array, which is exactly a row
    // filter. The loader usually leaves a morton permutation in place, so compose
    // the two orders and wrap its parent - one gather on the file rather than two.
    let source: ChunkSource;
    if (retained instanceof PermutedChunkSource) {
        const composed = new Uint32Array(rows.length);
        for (let i = 0; i < rows.length; ++i) {
            composed[i] = retained.order[rows[i]];
        }
        source = new PermutedChunkSource(retained.parent, composed);
    } else {
        source = new PermutedChunkSource(retained, rows);
    }

    // relabel as PLY space so the writer bakes nothing (see above)
    const raw: ChunkSource = {
        meta: { ...source.meta, transform: Transform.PLY.clone(), shBands: Math.min(source.meta.shBands, settings?.maxSHBands ?? 3) as SHBands },
        read: request => source.read(request),
        close: () => source.close()
    };

    const pool = createChunkDataPool({ chunkSize: raw.meta.chunkSize });
    let order = Uint32Array.from(rows, (_row, index) => index);
    let capturedOrder = false;
    try {
        const targetFs = settings?.format === 'sog' ? attributionFileSystem(fs) : fs;
        if (settings?.format === 'sog') {
            const attributed = targetFs as ReturnType<typeof attributionFileSystem>;
            const gather = attributed.jsiSogAttribution;
            attributed.jsiSogAttribution = (input, inputPool, permutation, outputFilename) => {
                order = permutation.slice();
                capturedOrder = true;
                return gather(input, inputPool, permutation, outputFilename);
            };
        }
        await writeSource({ filename,
            outputFormat: settings?.format === 'sog' ? 'sog-bundle' : 'ply',
            source: raw,
            pool,
            options: { iterations: settings?.iterations ?? 10 },
            createDevice: createGpuDevice }, targetFs);
        if (settings?.format === 'sog' && !capturedOrder) throw new Error('SOG project conversion requires the supported row-order integration.');
        if (order.length !== rows.length) throw new Error('Invalid converted project resource ordering.');
        if (settings?.format === 'sog') {
            const seen = new Uint8Array(rows.length);
            for (const row of order) {
                if (row >= rows.length || seen[row]) throw new Error('Invalid converted project resource ordering.');
                seen[row] = 1;
            }
        }
        return order;
    } finally {
        pool.destroy();
    }
};

/**
 * Extract Splat data into a DataTable for use with splat-transform writers.
 * This is shared between serializeSog and serializeViewer.
 */
// Bridge splat-transform progress events to supersplat's events.
const createProgressRenderer = (header: string, events?: Events): Renderer => ({
    handle: (event: LogEvent) => {
        switch (event.kind) {
            case 'scopeStart':
                if (event.depth === 0) {
                    events?.fire('progressStart', header);
                } else {
                    events?.fire('progressUpdate', {
                        text: event.index !== undefined && event.total !== undefined ?
                            `Step ${event.index} of ${event.total}: ${event.name}` :
                            event.name,
                        progress: 0
                    });
                }
                break;
            case 'scopeEnd':
                if (event.depth === 0) {
                    events?.fire('progressEnd');
                }
                break;
            case 'barStart':
                events?.fire('progressUpdate', { text: event.name, progress: 0 });
                break;
            case 'barTick':
                events?.fire('progressUpdate', {
                    progress: event.total > 0 ? 100 * event.current / event.total : 0
                });
                break;
            case 'barEnd':
                events?.fire('progressUpdate', { progress: 100 });
                break;
            case 'message':
                if (event.level === 'error') console.error(event.text);
                else if (event.level === 'warn') console.warn(event.text);
                else if (event.level === 'info') console.info(event.text);
                else if (event.level === 'debug') console.debug(event.text);
                break;
            case 'output':
                console.log(event.text);
                break;
        }
    }
});

const serializeViewer = async (splats: Splat[], serializeSettings: SerializeSettings, options: ViewerExportSettings, fs: FileSystem): Promise<void> => {
    const { experienceSettings, events } = options;
    const viewerSerializeSettings = {
        ...serializeSettings,
        minOpacity: 1 / 255,
        removeInvalid: true
    };

    splatTransformLogger.setRenderer(createProgressRenderer(options.content === 'streamed-lod' ? 'Exporting Streamed LOD Viewer' : 'Exporting HTML', events));

    // splat-transform's writers leave their top-level scope open on error
    // (their contract is for the caller to unwind), so we explicitly
    // unwind here to deliver a matching depth-0 `scopeEnd(failed)` to the
    // renderer. That fires `progressEnd` and dismisses the dialog before
    // any error popup is shown.
    try {
        if (options.content === 'streamed-lod') {
            const memFs = new MemoryFileSystem();

            const lodSource = options.lodSource === 'visible-splats' ?
                await createVisibleSplatLodSource(splats, viewerSerializeSettings, options.lodLevels, options.lodSplatIndices) :
                await createGeneratedLodSource(splats, viewerSerializeSettings, options.lodLevels, options.lodDecimationPercent);

            if (!lodSource) {
                return;
            }

            try {
                await writeLodSource({
                    filename: 'lod-meta.json',
                    mainSource: lodSource,
                    envSource: null,
                    iterations: 10,
                    createDevice: createGpuDevice,
                    chunkCount: 512,
                    chunkExtent: 16
                }, attributionFileSystem(memFs));
            } finally {
                await lodSource.close();
            }

            await writeJsiViewerPackage(memFs, experienceSettings, 'lod-meta.json');
            await writeZipFromMemory(memFs, fs);
        } else if (options.type === 'html') {
            // Single self-contained HTML using the custom JSI viewer.
            await writeJsiViewerHtml(splats, viewerSerializeSettings, experienceSettings, fs);
        } else {
            // Package - write the JSI viewer shell and a standalone SOG scene into a ZIP.
            const memFs = new MemoryFileSystem();
            await writeSplatFile(splats, viewerSerializeSettings, 'sog-bundle', 'scene.sog', { iterations: 10 }, memFs);
            await writeJsiViewerPackage(memFs, experienceSettings, 'scene.sog');
            await writeZipFromMemory(memFs, fs);
        }
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

// SOG serialization using splat-transform library

type SogSettings = SerializeSettings & {
    iterations: number;
    events?: Events;
};

const serializeSog = async (splats: Splat[], settings: SogSettings, fs: FileSystem): Promise<void> => {
    const { iterations = 10, events } = settings;

    splatTransformLogger.setRenderer(createProgressRenderer('Exporting SOG', events));

    // Streamed via writeSogSource — no whole-scene DataTable copy.
    // splat-transform's writers leave their top-level scope open on error
    // (their contract is for the caller to unwind), so we explicitly
    // unwind here to deliver a matching depth-0 `scopeEnd(failed)` to the
    // renderer. That fires `progressEnd` and dismisses the dialog before
    // any error popup is shown.
    try {
        await writeSplatFile(splats, settings, 'sog-bundle', 'output.sog', { iterations }, fs);
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

type SpzSettings = SerializeSettings & {
    version?: 3 | 4;
    events?: Events;
};

const serializeSpz = async (splats: Splat[], settings: SpzSettings, fs: FileSystem): Promise<void> => {
    const { version = 4, events } = settings;

    splatTransformLogger.setRenderer(createProgressRenderer('Exporting SPZ', events));

    // unwind the logger's top-level scope on error (see serializeSog)
    try {
        await writeSplatFile(splats, settings, 'spz', 'output.spz', { spzVersion: version }, fs);
    } catch (err) {
        splatTransformLogger.unwindAll(true);
        throw err;
    }
};

export {
    Writer,
    writeResourceFile,
    writeSplatFile,
    serializeSog,
    serializeSpz,
    serializeViewer,
    AnimTrack,
    Camera,
    Annotation,
    PostEffectSettings,
    CameraPoseOverlay,
    CameraPoseOverlayTransform,
    defaultPostEffectSettings,
    ExperienceSettings,
    SerializeSettings,
    SogSettings,
    SpzSettings,
    ViewerExportSettings,
    WebGPUUnavailableError
};
